import { z } from 'zod';
import { uuidSchema } from './common.js';
import type { FundBalanceSummary, MonthlyFlowPoint, TransactionResponse } from './transactions.js';

/**
 * RAG (Red/Amber/Green) traffic-light status for a fund's current balance
 * vs. its configured low-balance threshold.
 *
 *   RED   — balance ≤ threshold
 *   AMBER — threshold < balance ≤ threshold × 1.5
 *   GREEN — balance > threshold × 1.5
 */
export type RagStatus = 'RED' | 'AMBER' | 'GREEN';

/** One tile in the "All Funds at a Glance" row. */
export interface FundCard {
  fundId: string;
  fundCode: string;
  fundName: string;
  displayOrder: number;
  isActive: boolean;
  openingBalance: string;
  totalCredits: string;
  totalDebits: string;
  currentBalance: string;
  /** Low-balance threshold (BDT) configured in settings.low_balance_thresholds. */
  threshold: string;
  ragStatus: RagStatus;
  /** Last N months of net (credit − debit), oldest first. Plotted as the mini sparkline. */
  sparkline: number[];
}

export interface DashboardSummary {
  cards: FundCard[];
  /** ISO timestamp when the snapshot was computed (server time, UTC). */
  asOf: string;
}

/** Row 1 — Private Fund Spotlight. */
export interface PrivateSpotlight {
  fund: FundBalanceSummary;
  /**
   * Percent change in closing balance, this month vs last month. Rounded to
   * two decimals. `null` when there is no prior month to compare or when
   * last month's closing balance was exactly zero (avoids div-by-zero).
   */
  momPercent: number | null;
  /** 6-month running closing-balance series, oldest first. */
  sparkline: number[];
  /** Latest 10 transactions on the Private fund (most recent first). */
  latestTransactions: TransactionResponse[];
}

/**
 * Spotlight (Row 1) accepts an optional `fundCode` so the CO can switch the
 * hero between funds. Defaults to "PVT" so the original Private Spotlight
 * URL still works for back-compat and bookmarks.
 */
export const spotlightQuerySchema = z.object({
  fundCode: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Z0-9_-]+$/i, 'Fund code must be alphanumeric')
    .optional(),
});
export type SpotlightQuery = z.infer<typeof spotlightQuerySchema>;

export const monthlyFlowQuerySchema = z.object({
  fundId: uuidSchema,
  /** How many trailing months of flow to return, inclusive of the current Dhaka month. */
  months: z.coerce.number().int().min(1).max(36).default(12),
});
export type MonthlyFlowQuery = z.infer<typeof monthlyFlowQuerySchema>;

export const subHeadSpendQuerySchema = z.object({
  fundId: uuidSchema,
  /** FY start year — 2026 means FY 2026-27 (1 Jul 2026 → 30 Jun 2027). */
  fyYear: z.coerce.number().int().min(2000).max(2100),
});
export type SubHeadSpendQuery = z.infer<typeof subHeadSpendQuerySchema>;

export const budgetVsActualDashboardQuerySchema = z.object({
  fundId: uuidSchema,
  fyYear: z.coerce.number().int().min(2000).max(2100),
});
export type BudgetVsActualDashboardQuery = z.infer<typeof budgetVsActualDashboardQuerySchema>;

export interface MonthlyFlowDashboardResponse {
  fundId: string;
  fundCode: string;
  points: MonthlyFlowPoint[];
}
